import { isRecord } from "@tsmono/util";

/** Column value kind, selecting the filter editor + operator set. */
export type FilterType =
  | "string"
  | "number"
  | "boolean"
  | "date"
  | "datetime"
  | "duration"
  | "unknown";

export const UI_OPERATORS = [
  "contains",
  "does not contain",
  "starts with",
  "ends with",
  "=",
  "!=",
  "<",
  "<=",
  ">",
  ">=",
  "in",
  "not in",
  "between",
  "not between",
  "is blank",
  "is not blank",
] as const;

/**
 * UI-level filter operator — what the user picks in the popover. Compiled at
 * the query boundary into a wire `Condition` (log list) or a filtrex fragment
 * (samples page, future). Deliberately distinct from the generated
 * `OperatorModel` wire enum, which cannot carry UI-only operators like
 * `contains`.
 */
export type UiOperator = (typeof UI_OPERATORS)[number];

export const isUiOperator = (value: unknown): value is UiOperator =>
  UI_OPERATORS.some((operator) => operator === value);

/**
 * A single filter condition: the operator plus the raw input strings exactly
 * as typed, so reopening the editor restores what the user entered.
 * Compilation (and input parsing) happens at read time.
 */
export interface FilterCondition {
  operator: UiOperator;
  value: string;
  /** Range end for between/not between. */
  value2?: string;
}

/**
 * A column's filter: one condition, optionally joined with a second
 * (AG-style two-condition filter). Single-condition specs omit `join`/
 * `second` and are shape-identical to the pre-pair model.
 */
export interface FilterSpec extends FilterCondition {
  join?: "and" | "or";
  second?: FilterCondition;
}

/** A single column's active filter (keyed by columnId in grid state). */
export interface ColumnFilter {
  columnId: string;
  filterType: FilterType;
  spec: FilterSpec;
}

const isConditionShaped = (value: unknown): value is FilterCondition => {
  if (!isRecord(value)) return false;
  const c = value;
  return (
    isUiOperator(c.operator) &&
    typeof c.value === "string" &&
    (c.value2 === undefined || typeof c.value2 === "string")
  );
};

/**
 * Runtime guard for entries read from persisted grid state. Pre-FilterSpec
 * builds stored a compiled `condition` instead of a `spec`; those entries are
 * unusable anyway (JSON rehydration strips the ConditionBuilder prototype, so
 * `.and()` would crash) and are dropped by callers using this guard. When
 * `join`/`second` are present they must be consistent (both or neither) and
 * `second` must itself be condition-shaped.
 */
export const isColumnFilter = (value: unknown): value is ColumnFilter => {
  if (!isRecord(value)) return false;
  const v = value;
  if (typeof v.columnId !== "string" || typeof v.filterType !== "string") {
    return false;
  }
  const spec = v.spec;
  if (!isRecord(spec) || !isConditionShaped(spec)) return false;

  const hasJoin = spec.join !== undefined;
  const hasSecond = spec.second !== undefined;
  if (hasJoin !== hasSecond) return false;
  if (hasJoin && spec.join !== "and" && spec.join !== "or") return false;
  if (hasSecond && !isConditionShaped(spec.second)) return false;

  return true;
};
